import { useCallback } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useAppTourStore } from '../store/useAppTourStore';
import useMediaQuery from './useMediaQuery';
import { DesktopSteps, MobileSteps } from '../components/AppTour/appTour.config';
import { useAppStore } from '../store/useAppStore';
import { usePostModalStore } from '../store/usePostModalStore';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants for step indices
// Note: These indices reflect the nominal step positions. Joyride automatically skips
// steps when targets are not found (e.g., post cards when no posts exist).
const STEP_INDEX = {
  // Desktop indices
  DESKTOP: {
    DASHBOARD: 0,
    CAMPAIGN_AGENT_CTA: 1,
    CAMPAIGN_AGENT_PAGE: 2,
    CAMPAIGN_AGENT_COUNTS: 3,
    EMPTY_STATE: 4, // Shown when no posts exist
    STORY_CARD: 5, // Single "post card" step — opens whichever card (story or feed) exists
    POST_MODAL_PREVIEW: 6,
    POST_MODAL_START: 7,
    POST_MODAL_SCHEDULE: 8,
    POST_MODAL_REGENERATE: 9,
    POST_MODAL_END: 9,
    CALENDAR: 10,
    CALENDAR_EVENT: 11,
    CALENDAR_CHANGE_VIEW: 12,
    BRAND_NAV: 13,
    BRAND_ABOUT: 14,
    BRAND_OFFERINGS_TAB: 15,
    BRAND_OFFERINGS: 16,
  },
  // Mobile indices (consolidated modal steps)
  MOBILE: {
    DASHBOARD: 0,
    CAMPAIGN_AGENT_CTA: 1,
    CAMPAIGN_AGENT_PAGE: 2,
    CAMPAIGN_AGENT_COUNTS: 3,
    DASHBOARD_POSTS: 4,
    POST_MODAL_PREVIEW: 5,
    POST_MODAL_START: 6,
    POST_MODAL_END: 6,
    CALENDAR: 7,
    CALENDAR_EVENT: 8,
    CALENDAR_CHANGE_VIEW: 9,
    BRAND_NAV: 10,
    BRAND_ABOUT: 11,
    BRAND_OFFERINGS_TAB: 12,
    BRAND_OFFERINGS: 13,
  },
};

const DELAYS = {
  MODAL_OPEN: 300,
  SIDEBAR_OPEN: 300,
  NAVIGATION: 500, // Extra time for route changes and DOM updates
};

// Helper: Get sidebar open steps based on device
const getMobileSidebarOpenSteps = (isMobileView) => {
  const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
  return [
    INDEX.CALENDAR,
    INDEX.DASHBOARD,
    INDEX.BRAND_NAV,
  ];
};

// Helper: Get sidebar closed steps based on device
const getMobileSidebarClosedSteps = (isMobileView) => {
  const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
  return [
    INDEX.CAMPAIGN_AGENT_CTA,
    INDEX.CAMPAIGN_AGENT_PAGE,
    INDEX.CAMPAIGN_AGENT_COUNTS,
    INDEX.DASHBOARD_POSTS,
    INDEX.EMPTY_STATE,
    INDEX.STORY_CARD,
    INDEX.FEED_CARD,
    INDEX.POST_MODAL_PREVIEW,
    INDEX.POST_MODAL_START,
    INDEX.CALENDAR_EVENT,
    INDEX.CALENDAR_CHANGE_VIEW,
    INDEX.BRAND_ABOUT,
    INDEX.BRAND_OFFERINGS_TAB,
    INDEX.BRAND_OFFERINGS,
  ].filter((step) => step !== undefined);
};

// Helper: Get card scale steps
const getCardScaleSteps = () => [];

// Helper: Detect available post cards on dashboard
const detectAvailablePosts = () => {
  const hasStoryCard = !!document.querySelector('.tour-card-for-story');
  const hasFeedCard = !!document.querySelector('.tour-card-for-feed');
  const hasRegeneratableCard = !!document.querySelector('.tour-card-can-regenerate');
  const hasAnyCard = hasStoryCard || hasFeedCard;
  const hasBothCards = hasStoryCard && hasFeedCard;
  return { hasStoryCard, hasFeedCard, hasAnyCard, hasBothCards, hasRegeneratableCard };
};

// Helper: Detect if calendar has any events
const detectCalendarEvents = () => {
  const hasEvents = !!document.querySelector(
    '.mobile-tour-calendar-event, .desktop-tour-calendar-event'
  );
  return { hasEvents };
};

/**
 * Centralized hook for Joyride state and handlers.
 * Relies on persisted app tour store for run/step tracking.
 */
export const useAppTour = () => {
  const { run, stepIndex, startTour, setStepIndex, setRunAndStep, completeTour, tourKey } =
    useAppTourStore();
  const { setSidebarOpen, sidebarOpen } = useAppStore();
  const { isSmallMobile, isMobile, isTablet } = useMediaQuery();
  const { isOpen: isPostModalOpen, closeModal } = usePostModalStore();
  const isMobileView = isSmallMobile || isMobile || isTablet;
  const navigate = useNavigate();
  const location = useLocation();

  // Select steps based on device type (disableBeacon is already set in config)
  const steps = isMobileView ? MobileSteps : DesktopSteps;

  // Helper: Remove scaling from all elements
  const removeScale = () => {
    document.querySelectorAll('.tour-scale-active').forEach((el) => {
      el.classList.remove('tour-scale-active');
    });
  };

  // Helper: Apply scale to card elements
  const applyScaleToElement = (element, index) => {
    const cardScaleSteps = getCardScaleSteps(isMobileView);
    if ((isSmallMobile || isMobile) && cardScaleSteps.includes(index)) {
      element.classList.add('tour-scale-active');
    }
  };

  // Helper: Manage sidebar state based on step
  const updateSidebarForStep = (index) => {
    if (!isMobileView) return;

    const sidebarOpenSteps = getMobileSidebarOpenSteps(isMobileView);
    const sidebarClosedSteps = getMobileSidebarClosedSteps(isMobileView);

    if (sidebarOpen && sidebarClosedSteps.includes(index)) {
      setSidebarOpen(false);
    } else if (!sidebarOpen && sidebarOpenSteps.includes(index)) {
      setSidebarOpen(true);
    }
  };

  // Handler: Tour finished or skipped
  const handleTourEnd = useCallback(() => {
    removeScale();
    completeTour('default');
    if (sidebarOpen) setSidebarOpen(false);
    if (isPostModalOpen) closeModal();
    if (location.pathname !== '/dashboard') navigate('/dashboard');
  }, [closeModal, completeTour, isPostModalOpen, location.pathname, navigate, setSidebarOpen, sidebarOpen]);

  // Helper: Perform action with delay and advance step (with smart skipping)
  const delayedStepTransition = (index, action, delay = DELAYS.MODAL_OPEN) => {
    action();
    setTimeout(() => {
      const nextValidIndex = getNextValidStepIndex(index);
      if (nextValidIndex >= steps.length) {
        handleTourEnd();
        return;
      }
      setStepIndex(nextValidIndex);
    }, delay);
    return true; // Prevent default increment
  };

  const navigateAndResume = (path, targetIndex, { openSidebar = false } = {}) => {
    setRunAndStep(false, targetIndex);
    navigate(path);
    setTimeout(() => {
      setSidebarOpen(openSidebar);
      setTimeout(() => setRunAndStep(true, targetIndex), DELAYS.SIDEBAR_OPEN);
    }, DELAYS.NAVIGATION);
    return true;
  };

  // Helper: Find next valid step index, skipping unavailable targets
  const getNextValidStepIndex = (currentIndex) => {
    const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
    const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
    const { hasEvents } = detectCalendarEvents();

    let nextIndex = currentIndex + 1;

    // Skip empty state if posts exist
    if (nextIndex === INDEX.EMPTY_STATE && hasAnyCard) {
      nextIndex++;
    }

    // Skip the post-card step if there are no post cards at all
    if (nextIndex === INDEX.STORY_CARD && !hasAnyCard) {
      nextIndex++;
    }

    // Skip calendar event step if no events exist
    if (nextIndex === INDEX.CALENDAR_EVENT && !hasEvents) {
      nextIndex++;
    }

    return nextIndex;
  };

  // Helper: Find previous valid step index, skipping unavailable targets
  const getPrevValidStepIndex = (currentIndex) => {
    const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
    const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
    const { hasEvents } = detectCalendarEvents();

    let prevIndex = currentIndex - 1;

    // Skip calendar event step if no events exist (when going backwards)
    if (prevIndex === INDEX.CALENDAR_EVENT && !hasEvents) {
      prevIndex--;
    }

    // Skip the post-card step if there are no post cards at all (going backwards)
    if (prevIndex === INDEX.STORY_CARD && !hasAnyCard) {
      prevIndex--;
    }

    // Skip empty state if posts exist (when going backwards)
    if (prevIndex === INDEX.EMPTY_STATE && hasAnyCard) {
      prevIndex--;
    }

    return prevIndex;
  };

  // Handler: Before step shows
  const handleStepBefore = useCallback(
    (step, index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
      const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
      const { hasEvents } = detectCalendarEvents();

      // Skip empty state step if any posts exist — jump to the post-card step
      if (index === INDEX.EMPTY_STATE && hasAnyCard) {
        setStepIndex(INDEX.STORY_CARD);
        return;
      }

      // Skip the post-card step entirely if there are no post cards
      if (index === INDEX.STORY_CARD && !hasAnyCard) {
        setStepIndex(index + 1);
        return;
      }

      // Skip calendar event step if no events exist
      if (index === INDEX.CALENDAR_EVENT && !hasEvents) {
        setStepIndex(index + 1); // Skip to calendar change view
        return;
      }

      removeScale();

      const element = document.querySelector(step?.target || '');
      if (!element) return;

      applyScaleToElement(element, index);
      updateSidebarForStep(index);
    },
    [isMobile, isSmallMobile, isMobileView, sidebarOpen, setStepIndex]
  );

  const handleTargetMissing = useCallback(
    (index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;

      if (index === INDEX.POST_MODAL_START || index === INDEX.POST_MODAL_END) {
        const targetIndex = INDEX.CALENDAR;
        if (isPostModalOpen) closeModal();
        setRunAndStep(false, targetIndex);
        navigate('/schedule');
        setTimeout(() => {
          setSidebarOpen(isMobileView);
          setRunAndStep(true, targetIndex);
        }, DELAYS.NAVIGATION);
        return;
      }

      const nextValidIndex = getNextValidStepIndex(index);
      if (nextValidIndex >= steps.length) {
        handleTourEnd();
        return;
      }
      setStepIndex(nextValidIndex === index ? index + 1 : nextValidIndex);
    },
    [closeModal, handleTourEnd, isMobileView, isPostModalOpen, navigate, setRunAndStep, setSidebarOpen, setStepIndex, steps.length]
  );

  // Handler: Navigate to next step
  const handleNextStep = useCallback(
    (index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
      const { hasStoryCard, hasFeedCard, hasAnyCard, hasRegeneratableCard } = detectAvailablePosts();

      if (index === INDEX.CAMPAIGN_AGENT_CTA) {
        return navigateAndResume('/create', INDEX.CAMPAIGN_AGENT_PAGE);
      }

      if (index === INDEX.CAMPAIGN_AGENT_COUNTS) {
        return navigateAndResume('/dashboard', INDEX.DASHBOARD_POSTS ?? INDEX.EMPTY_STATE, { openSidebar: false });
      }

      if (index === INDEX.DASHBOARD_POSTS) {
        if (!hasAnyCard || !hasRegeneratableCard) {
          return navigateAndResume('/schedule', INDEX.CALENDAR, { openSidebar: isMobileView });
        }
        const card = document.querySelector('.tour-card-can-regenerate');
        if (card) {
          return delayedStepTransition(index, () => card.click(), DELAYS.NAVIGATION);
        }
      }

      // Handle empty state - continue to schedule when no posts exist
      if (index === INDEX.EMPTY_STATE && !hasAnyCard) {
        return navigateAndResume('/schedule', INDEX.CALENDAR, { openSidebar: isMobileView });
      }

      // Open the post (page/modal) from the single post-card step — whichever
      // card exists (story preferred, feed as fallback).
      if (index === INDEX.STORY_CARD && !isPostModalOpen && hasAnyCard) {
        const card =
          document.querySelector('.tour-card-for-story') ||
          document.querySelector('.tour-card-for-feed');
        if (card) {
          return delayedStepTransition(index, () => card.click(), DELAYS.NAVIGATION);
        }
        return false;
      }

      // Navigate to schedule after post details (mobile goes directly, desktop has more post steps)
      if (isMobileView && index === INDEX.POST_MODAL_START) {
        return delayedStepTransition(
          index,
          () => {
            closeModal();
            navigate('/schedule');
            setSidebarOpen(true);
          },
          DELAYS.NAVIGATION
        );
      }

      // Desktop: Navigate to schedule after all post-detail steps
      if (!isMobileView && index === INDEX.POST_MODAL_END) {
        return delayedStepTransition(
          index,
          () => {
            closeModal();
            navigate('/schedule');
            setSidebarOpen(false);
          },
          DELAYS.NAVIGATION
        );
      }

      // Close sidebar for calendar event
      if (index === INDEX.CALENDAR) {
        return delayedStepTransition(index, () => setSidebarOpen(false), DELAYS.SIDEBAR_OPEN);
      }

      // Navigate to profile with sidebar open
      if (index === INDEX.CALENDAR_CHANGE_VIEW) {
        return delayedStepTransition(
          index,
          () => {
            navigate('/brand');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      if (index === INDEX.BRAND_NAV) {
        return delayedStepTransition(index, () => setSidebarOpen(false), DELAYS.SIDEBAR_OPEN);
      }

      if (index === INDEX.BRAND_OFFERINGS_TAB) {
        const offeringsTab = document.querySelector("button[data-tab-btn='offerings']");
        if (offeringsTab) {
          return delayedStepTransition(index, () => offeringsTab.click(), DELAYS.SIDEBAR_OPEN);
        }
      }

      if (index === INDEX.BRAND_OFFERINGS) {
        return delayedStepTransition(
          index,
          () => {
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Default: just advance to next valid step
      const nextValidIndex = getNextValidStepIndex(index);
      if (nextValidIndex >= steps.length) {
        handleTourEnd();
        return true;
      }
      updateSidebarForStep(nextValidIndex);
      setStepIndex(nextValidIndex);
      return false;
    },
    [isPostModalOpen, isMobileView, isMobile, isSmallMobile, sidebarOpen]
  );

  // Handler: Navigate to previous step
  const handlePrevStep = useCallback(
    (index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;

      // Close post modal when going back from preview to the appropriate card
      if (index === INDEX.POST_MODAL_PREVIEW && isPostModalOpen) {
        closeModal();
        const prevValidIndex = getPrevValidStepIndex(index);
        setTimeout(() => setStepIndex(prevValidIndex), DELAYS.MODAL_OPEN);
        return true;
      }

      if (index === INDEX.POST_MODAL_PREVIEW && isMobileView) {
        return navigateAndResume('/dashboard', INDEX.DASHBOARD_POSTS ?? INDEX.EMPTY_STATE);
      }

      // Navigate back when going back from story card
      if (index === INDEX.STORY_CARD) {
        const { hasAnyCard } = detectAvailablePosts();
        if (hasAnyCard) {
          return navigateAndResume('/create', INDEX.CAMPAIGN_AGENT_COUNTS);
        }

        const targetIndex = INDEX.EMPTY_STATE;

        if (isMobileView) {
          setSidebarOpen(true);
          setTimeout(() => setStepIndex(targetIndex), DELAYS.SIDEBAR_OPEN);
        } else {
          setStepIndex(targetIndex);
        }
        return true;
      }

      if (index === INDEX.CAMPAIGN_AGENT_PAGE) {
        return navigateAndResume('/dashboard', INDEX.CAMPAIGN_AGENT_CTA);
      }

      if (index === INDEX.EMPTY_STATE) {
        return navigateAndResume('/create', INDEX.CAMPAIGN_AGENT_COUNTS);
      }

      if (index === INDEX.DASHBOARD_POSTS) {
        return navigateAndResume('/create', INDEX.CAMPAIGN_AGENT_COUNTS);
      }

      if (index === INDEX.CALENDAR && location.pathname === '/schedule') {
        return navigateAndResume('/dashboard', INDEX.DASHBOARD_POSTS ?? INDEX.EMPTY_STATE);
      }

      // Open sidebar when going back to calendar from event
      if (index === INDEX.CALENDAR_EVENT) {
        setSidebarOpen(true);
        setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
        return true;
      }

      if (index === INDEX.BRAND_NAV && location.pathname === '/brand') {
        return navigateAndResume('/schedule', INDEX.CALENDAR_CHANGE_VIEW);
      }

      if (index === INDEX.BRAND_OFFERINGS_TAB) {
        const summaryTab = document.querySelector("button[data-tab-btn='about']");
        if (summaryTab) {
          summaryTab.click();
          setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
          return true;
        }
      }

      // Default: go back to previous valid step
      const prevValidIndex = getPrevValidStepIndex(index);
      updateSidebarForStep(prevValidIndex);
      setStepIndex(prevValidIndex);
      return false;
    },
    [isMobileView, isPostModalOpen, location.pathname, sidebarOpen, isMobile, isSmallMobile]
  );

  // Handler: After step action
  const handleStepAfter = useCallback(
    (action, index) => {
      removeScale();

      switch (action) {
        case ACTIONS.NEXT:
          return handleNextStep(index);

        case ACTIONS.PREV:
          return handlePrevStep(index);

        default:
          return false;
      }
    },
    [handleNextStep, handlePrevStep]
  );

  // Main Joyride callback
  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, status, type, index, step } = data;

      // Handle tour end states
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        handleTourEnd();
        return;
      }

      // Handle step events
      switch (type) {
        case EVENTS.STEP_BEFORE:
          handleStepBefore(step, index);
          break;

        case EVENTS.STEP_AFTER:
          handleStepAfter(action, index);
          break;

        case EVENTS.TARGET_NOT_FOUND:
          // When a target isn't found, Joyride automatically skips to the next step
          // This happens when post cards don't exist on dashboard
          break;

        case EVENTS.TOUR_END:
          handleTourEnd();
          break;

        default:
          // Ignore other events
          break;
      }
    },
    [handleTourEnd, handleStepBefore, handleStepAfter]
  );

  return {
    steps,
    run,
    stepIndex,
    startTour,
    handleJoyrideCallback,
    handleTargetMissing,
    tourKey,
    isMobile,
    isTablet,
    isMobileView,
  };
};

export default useAppTour;
